// Client for api/unanswered_questions.php — the to-do list of real guest
// questions the chat widget couldn't confidently answer from the FAQ
// database, surfaced on /admin/faq so coverage grows from what guests
// actually asked.

import { parseJsonResponse } from './apiFetch';
const API_BASE: string = import.meta.env.VITE_API_BASE ?? '/api';

export interface UnansweredQuestion {
  id: number;
  question: string;
  asked_count: number;
  first_asked_at: string;
  last_asked_at: string;
}

/** Fire-and-forget, called from ChatWidget.tsx whenever it shows a guest
 * its "couldn't find a confident answer" fallback. Never awaited for UI
 * purposes and never surfaces a failure to the guest — a logging hiccup
 * here must not affect the chat experience itself. */
export function logUnansweredQuestion(question: string): void {
  fetch(`${API_BASE}/unanswered_questions.php?action=log`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ question }),
  }).catch(() => {});
}

export async function fetchUnansweredQuestions(): Promise<UnansweredQuestion[]> {
  const res = await fetch(`${API_BASE}/unanswered_questions.php?action=list`, { credentials: 'include' });
  const data = await parseJsonResponse(res);
  if (!data.success) throw new Error(data.message ?? 'Failed to load unanswered questions');
  return data.items;
}

/** Removes one or more entries — used both when an admin turns a question
 * into a real FAQ (closing the loop automatically) and when they just
 * dismiss one that doesn't need an answer (spam, a duplicate, etc.). */
export async function deleteUnansweredQuestions(ids: number[], csrfToken: string): Promise<{ success: boolean; message?: string }> {
  const res = await fetch(`${API_BASE}/unanswered_questions.php?action=delete`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids, csrf_token: csrfToken }),
  });
  return parseJsonResponse(res);
}
